import { CloseOutlined, MenuOutlined } from '@ant-design/icons';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, Flex } from 'antd';
import { FC, useCallback, useMemo } from 'react';

interface Item {
  label: string;
  value: string;
  selected?: boolean;
  id: string;
  order?: number | null;
}

interface SortableListProps {
  items: Item[];
  onChange: (items: Item[]) => void;
  onDelete: (items: Item[]) => void;
}

/**
 * A sortable list component with drag-and-drop functionality
 * Uses dnd-kit for accessible drag-and-drop operations
 */
export const SortableList: FC<SortableListProps> = ({
  items = [],
  onChange,
  onDelete,
}) => {
  // Initialize sensors for pointer-based drag interactions
  const sensors = useSensors(useSensor(PointerSensor));

  /**
   * Memoized sorted items array
   * - Sorts items by their order property
   * - Ensures consistent order numbering
   */
  const sortedItems = useMemo(() => {
    return items
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((item, index) => ({
        ...item,
        order: index + 1,
      }));
  }, [items]);

  /**
   * Handles the completion of a drag operation
   * - Reorders items based on drag position
   * - Updates the order property of all items
   */
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (over && active.id !== over.id) {
        const oldIndex = sortedItems.findIndex((item) => item.id === active.id);
        const newIndex = sortedItems.findIndex((item) => item.id === over.id);

        const reorderedItems = arrayMove(sortedItems, oldIndex, newIndex).map(
          (item, index) => ({
            ...item,
            order: index + 1,
          })
        );

        onChange(reorderedItems);
      }
    },
    [onChange, sortedItems]
  );

  /**
   * Handles item deletion
   * - Removes the item from the list
   * - Calls the onDelete callback with updated list
   */
  const handleDelete = useCallback(
    (id: string) => {
      const updatedItems = sortedItems.filter((item) => item.id !== id);
      onDelete(updatedItems);
    },
    [onDelete, sortedItems]
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={sortedItems.map((item) => item.id)}
        strategy={verticalListSortingStrategy}
      >
        <ul className="list-none p-0">
          {sortedItems.map((item) => (
            <SortableItem
              key={item.id}
              id={item.id}
              content={item.label}
              onDelete={handleDelete}
            />
          ))}
        </ul>
      </SortableContext>
    </DndContext>
  );
};

interface SortableItemProps {
  id: string;
  content: string;
  onDelete: (id: string) => void;
}

/**
 * A single item in the SortableList component
 * - Renders a single li element with a drag handle and content
 * - Handles deletion of the item via the onDelete callback
 * - Uses dnd-kit's useSortable hook for drag-and-drop functionality
 */
const SortableItem: FC<SortableItemProps> = ({ id, content, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    margin: '5px',
    backgroundColor: '#F9FAFB',
  };

  return (
    <li
      className="flex items-center justify-between p-2.5 rounded-md"
      ref={setNodeRef}
      style={style}
      {...attributes}
    >
      <Flex align="center">
        <span {...listeners} className="cursor-grab mr-3">
          <MenuOutlined />
        </span>
        {content}
      </Flex>
      <Button
        size="small"
        shape="circle"
        onClick={() => onDelete(id)}
        icon={<CloseOutlined />}
      />
    </li>
  );
};
